// ЖИВЫЕ ДЕЙСТВИЯ КАРТОЧКИ: лента событий, комментарии, свои поля, смена стадии.
//
// ЕДИНСТВЕННАЯ ТОЧКА СТЫКОВКИ С ЯДРОМ: страницы зовут только эти функции и о путях
// не знают. Ручки — настоящие, накатаны на боевом (cards_api.py, cards_write_api.py,
// card_feed.py, cards_fields.py; БЕЛЫЕ СПИСКИ BFF: _КАРТОЧКИ_ПУТИ и
// _КАРТОЧКИ_ЗАПИСЬ_ПУТИ в web/bff/main.py). Все формы ответов ниже ПРОВЕРЕНЫ
// curl-пробоями на боевом 30.07.2026 — числа и поля из проб записаны в комментариях
// у каждого пути.
//
// ДВА ПРОСТРАНСТВА НОМЕРОВ. Карточка живёт на bitrix_id (адрес /deals/107623), а ленте,
// своим полям и записи нужен id СВОЕЙ схемы. Его приносит сама карточка: /api/own/deal
// кладёт его в `_OWN.id` (проба: сделка bitrix 100322 → _OWN.id 68768; поля `rev` в
// ответе /own/deal НЕТ — ревизию перед записью читает /cards/record). Когда своего id
// нет (источник — зеркало /api/bx, у него блока _OWN не бывает), функции отвечают
// честным «не подключено» БЕЗ похода в сеть — экран говорит это словами, а не
// притворяется, что сохранил.
//
// АВТОР В ТЕЛЕ НЕ ПЕРЕДАЁТСЯ. BFF затирает пришедшие из браузера author*/via и
// подставляет автора из сессии (web/bff/main.py, cards_write_route). Посылать его
// отсюда бессмысленно и вредно: представиться из браузера нельзя.
//
// В Bitrix этим файлом не пишется НИЧЕГО: /cards-write/* работает только со своей
// схемой (прежний write-through /api/deal/update отсюда убран намеренно).
import { setDealExt, Unauthorized } from './client'

/** Страница ленты — как везде у ядра (cards_api._лимит, умолчание 50). */
const СТРАНИЦА = 50

/** Язык /cards/*: русские коды сущностей (cards_fields.СУЩНОСТИ_ПОЛЕЙ). */
const СУЩНОСТЬ_КАРТОЧКИ: Record<'deal', string> = { deal: 'сделка' }

/** Свой id записи: number из _OWN.id либо «не знаем» (зеркало, блока _OWN нет). */
export type СвойId = number | null | undefined

const НЕТ_СВОЕГО_ID = 'У карточки не видно записи своей схемы (она прочитана из зеркала '
  + 'Bitrix, без блока _OWN)'

// ─────────────────────────── транспорт ───────────────────────────

interface ОтветЯдра {
  status: number
  тело: Record<string, unknown>
}

/**
 * Свой вызов, а не request() из client.ts: тому нужен только успех, а здесь отказ —
 * полноценный ответ (409 несёт rev/who/when, 400 refused — fields по полям), и глотать
 * его до строки-сообщения нельзя.
 */
async function вызов(путь: string, init?: RequestInit): Promise<ОтветЯдра> {
  const res = await fetch(путь, {
    ...init,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
  })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  let тело: Record<string, unknown> = {}
  try {
    тело = текст ? (JSON.parse(текст) as Record<string, unknown>) : {}
  } catch {
    тело = {}
  }
  return { status: res.status, тело }
}

/** Отказ ядра — словами для человека: why написан для экрана, error — машинный код. */
const почемуИзТела = (о: ОтветЯдра): string =>
  String(о.тело.why ?? '') || String(о.тело.error ?? '') || `HTTP ${о.status}`

// ─────────────────────────── лента ───────────────────────────

/** Событие ленты — то, что рисует Timeline. */
export interface СобытиеЛенты {
  id: string | number
  /** ISO-время события (`ts` ядра). null — событие без даты, уходит в конец. */
  at: string | null
  /** comment | field | stage | system | activity. */
  kind: string
  /** Автор словами: ядро отдаёт {id, name} | null, здесь остаётся имя. */
  author?: string
  text: string
  origin: 'crm' | 'bitrix'
  /** Подробности события как отдаёт ядро (stage_id, from/to и т.п.). */
  data?: Record<string, unknown>
}

export interface ОтветЛенты {
  items: СобытиеЛенты[]
  total: number
  /** offset следующей страницы; null — дальше ничего нет. */
  next: number | null
  /** false — ленту спросить нечем (нет своего id) или ядро отказало: items пусты. */
  подключено: boolean
  /** Почему пусто — словами для экрана. */
  почему?: string
}

/**
 * Строка ленты ядра → событие Timeline. Форма строки — проба на боевом 30.07.2026
 * (сделка own id 68768, total 23): {id: 'bx-comment:5022620' | 'bx-stage:549566' | 'crm:…',
 * origin, kind, ts: '2025-12-11T11:34:42+00:00', author: {id, name} | null, text, data}.
 */
function событиеИзСтроки(r: Record<string, unknown>): СобытиеЛенты {
  const автор = r.author as { id?: unknown; name?: unknown } | null | undefined
  const имя = автор && typeof автор === 'object' ? String(автор.name ?? '') : ''
  return {
    id: (r.id as string | number) ?? '',
    at: r.ts == null ? null : String(r.ts),
    kind: String(r.kind ?? ''),
    author: имя || undefined,
    text: String(r.text ?? ''),
    origin: r.origin === 'crm' ? 'crm' : 'bitrix',
    data: (r.data && typeof r.data === 'object' ? r.data : {}) as Record<string, unknown>,
  }
}

/**
 * Слитая лента карточки: свои события card_feed + зеркальная история Bitrix.
 *
 * GET /api/cards/feed?entity=сделка&id=<свой>&limit=50&offset=<start>
 *   → {rows: […], total, limit, offset}   (card_feed.читать; проверено пробой:
 *     total 23 у сделки 68768, свежие первыми, offset режет после слияния).
 * Кириллица в entity уходит через encodeURIComponent — иначе query побьётся.
 */
export async function лентаКарточки(
  entity: 'deal', свойId: СвойId, start = 0,
): Promise<ОтветЛенты> {
  if (!свойId) {
    return {
      items: [], total: 0, next: null, подключено: false,
      почему: `${НЕТ_СВОЕГО_ID} — лента ядра не запрошена; показано только то, `
        + 'что известно самой карточке.',
    }
  }
  const о = await вызов(
    `/api/cards/feed?entity=${encodeURIComponent(СУЩНОСТЬ_КАРТОЧКИ[entity])}`
    + `&id=${свойId}&limit=${СТРАНИЦА}&offset=${start}`)
  if (о.status !== 200) {
    return { items: [], total: 0, next: null, подключено: false, почему: почемуИзТела(о) }
  }
  const rows = Array.isArray(о.тело.rows) ? (о.тело.rows as Record<string, unknown>[]) : []
  const total = Number(о.тело.total ?? rows.length) || 0
  const дальше = start + rows.length
  return {
    items: rows.map(событиеИзСтроки),
    total,
    next: rows.length > 0 && дальше < total ? дальше : null,
    подключено: true,
  }
}

// ─────────────────────────── комментарий ───────────────────────────

export interface ОтветКомментария {
  ok: boolean
  /** Почему не сохранён — словами ядра (cards_write.Отказ пишет для человека). */
  почему?: string
  /** Сохранённое событие — та же форма, что у чтения: лента дописывает его сама,
   *  не перечитываясь. */
  событие?: СобытиеЛенты
}

/**
 * POST /api/cards-write/comment-add {entity: 'сделка', id: <свой>, text}
 *   → 200 {event: строка ленты}  (card_feed.добавить_комментарий).
 * Отказ — 400 {error: 'refused', why, fields}: проверено пробой (пустой текст →
 * «Пустой комментарий не записывается: писать было нечего.», fields.text).
 */
export async function добавитьКомментарий(
  entity: 'deal', свойId: СвойId, text: string,
): Promise<ОтветКомментария> {
  if (!свойId) {
    return { ok: false, почему: `${НЕТ_СВОЕГО_ID} — комментарий записать некуда.` }
  }
  const о = await вызов('/api/cards-write/comment-add', {
    method: 'POST',
    body: JSON.stringify({ entity: СУЩНОСТЬ_КАРТОЧКИ[entity], id: свойId, text }),
  })
  if (о.status !== 200) return { ok: false, почему: почемуИзТела(о) }
  const событие = о.тело.event && typeof о.тело.event === 'object'
    ? событиеИзСтроки(о.тело.event as Record<string, unknown>)
    : undefined
  return { ok: true, событие }
}

// ─────────────────────────── свои поля (конструктор) ───────────────────────────

/** Определение своего поля — cards_fields._строка_определения. Виды совпадают
 *  с FieldType строки карточки один в один: text/number/date/select/checkbox/link. */
export interface СвоёПоле {
  code: string
  label: string
  kind: 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'link'
  options: string[]
  group: string | null
  sort: number
  required: boolean
}

export interface ОтветПолей {
  подключено: boolean
  почему?: string
  /** Живые (не архивные) определения из конструктора владельца. */
  custom: СвоёПоле[]
  /** {код поля: значение} этой записи; очищенные ядро не отдаёт вовсе. */
  values: Record<string, unknown>
}

/**
 * GET /api/cards/fields?entity=сделка&id=<свой>
 *   → {entity, system: […], custom: […], values: {…}}  (cards_fields.поля; без
 *     ?archived=1 архивные не приходят — форме они и не нужны).
 * Проверено пробой 30.07.2026: system 33 поля, custom 0 (владелец полей ещё не завёл,
 * и пустая секция с подсказкой — боевой случай, а не выдумка теста), values {}.
 */
export async function поляКарточки(entity: 'deal', свойId: СвойId): Promise<ОтветПолей> {
  if (!свойId) {
    return { подключено: false, почему: НЕТ_СВОЕГО_ID, custom: [], values: {} }
  }
  const о = await вызов(
    `/api/cards/fields?entity=${encodeURIComponent(СУЩНОСТЬ_КАРТОЧКИ[entity])}&id=${свойId}`)
  if (о.status !== 200) {
    return { подключено: false, почему: почемуИзТела(о), custom: [], values: {} }
  }
  const защита = (с: Record<string, unknown>): СвоёПоле => ({
    code: String(с.code ?? ''),
    label: String(с.label ?? с.code ?? ''),
    kind: (['text', 'number', 'date', 'select', 'checkbox', 'link'] as const)
      .find((в) => в === с.kind) ?? 'text',
    options: Array.isArray(с.options) ? (с.options as unknown[]).map(String) : [],
    group: с.group == null ? null : String(с.group),
    sort: Number(с.sort ?? 0) || 0,
    required: !!с.required,
  })
  const custom = Array.isArray(о.тело.custom)
    ? (о.тело.custom as Record<string, unknown>[]).map(защита)
    : []
  const values = (о.тело.values && typeof о.тело.values === 'object'
    ? о.тело.values : {}) as Record<string, unknown>
  return { подключено: true, custom, values }
}

/**
 * POST /api/cards-write/field-value-save {entity: 'сделка', id: <свой>, code, value}
 *   → 200 {entity, id, code, value, changed}  (cards_fields.сохранить_значение;
 *     событие 'field' в ленту ядро пишет само, той же транзакцией).
 * Отказ бросается словами ядра — их показывает строка поля (FieldRow ловит throw).
 * Проверено пробой: неизвестный код → 404 {'error': 'not_found',
 * 'why': "Поля 'probe_net_takogo' у «сделка» нет"}.
 */
export async function сохранитьЗначениеПоля(
  свойId: СвойId, code: string, value: unknown,
): Promise<{ изменено: boolean; значение: unknown }> {
  if (!свойId) throw new Error(`${НЕТ_СВОЕГО_ID} — значение сохранить некуда.`)
  const о = await вызов('/api/cards-write/field-value-save', {
    method: 'POST',
    body: JSON.stringify({ entity: СУЩНОСТЬ_КАРТОЧКИ.deal, id: свойId, code, value }),
  })
  if (о.status !== 200) throw new Error(почемуИзТела(о))
  return { изменено: !!о.тело.changed, значение: о.тело.value }
}

// ─────────────────────────── запись: стадия и системные поля ───────────────────────────

/**
 * STAGE_ID Bitrix → код стадии своей схемы. Обращает crm_own_api._стадия_наружу
 * (проверено там запросом на всех 51 стадии живых сделок): снять приставку «C4:»,
 * опустить регистр, чисто цифровому коду приписать «s» («C4:5» → «s5», «NEW» → «new»).
 * Полоса стадий живёт на кодах Bitrix из справочника ядра — переводим здесь,
 * чтобы экран двух пространств кодов не знал.
 */
export function кодСтадии(bitrixStageId: string): string {
  const голый = String(bitrixStageId ?? '').replace(/^C\d+:/, '')
  return /^[0-9]+$/.test(голый) ? `s${голый}` : голый.toLowerCase()
}

/**
 * Ревизия своей записи — перед каждой записью, свежая. /own/deal её не отдаёт
 * (проверено пробой), поэтому GET /api/cards/record?entity=сделка&id=<свой>
 *   → {row: {id, rev, state, values}}  (проба: id '68768', rev 2, state 'issued').
 * Чтение при действии, а не при открытии карточки: залежавшийся rev — это ложный 409.
 */
async function ревизия(свойId: number): Promise<number> {
  const о = await вызов(
    `/api/cards/record?entity=${encodeURIComponent(СУЩНОСТЬ_КАРТОЧКИ.deal)}&id=${свойId}`)
  if (о.status !== 200) {
    throw new Error(`ревизия записи не прочитана: ${почемуИзТела(о)}`)
  }
  const row = (о.тело.row ?? {}) as Record<string, unknown>
  const rev = Number(row.rev)
  if (!Number.isInteger(rev) || rev < 1) throw new Error('ревизия записи не прочитана')
  return rev
}

export type ИтогСтадии =
  | { ok: true; rev: number; стадия?: { from?: string | null; from_name?: string | null
      to?: string | null; to_name?: string | null } }
  | { ok: false
      /** conflict — успел другой; required — не хватает обязательных полей (values). */
      вид: 'conflict' | 'required' | 'refused'
      почему: string
      /** {код поля: что не так} — у вида required. */
      поля?: Record<string, string> }

/**
 * Смена стадии — в СВОЮ схему.
 *
 * POST /api/cards-write/stage-set {entity: 'deal', id: <свой>, rev, stage_id, values?}
 *   → 200 {id, rev, state, changed, row, stage: {from, from_name, to, to_name}}
 *     (card_feed.сменить_стадию: правка правилами cards_write + событие 'stage' в ленте;
 *      воронку ядро берёт из самой сделки — приписывать её здесь не нужно).
 * Отказы проверены пробоями 30.07.2026:
 *   409 {'error': 'conflict', 'why': 'Карточку уже изменили: у вас ревизия 1, в базе 2…',
 *        'rev': 2, 'who': null, 'when': null}  — who бывает null;
 *   400 {'error': 'refused', 'why', 'fields': {код: что не так}} — обязательность
 *        (lost_reason на проигрышной стадии): экран предлагает поле причины и повторяет
 *        вызов с values.
 */
export async function сменитьСтадию(
  свойId: СвойId, bitrixStageId: string, values?: Record<string, unknown>,
): Promise<ИтогСтадии> {
  if (!свойId) {
    return { ok: false, вид: 'refused', почему: `${НЕТ_СВОЕГО_ID} — стадию менять нечем.` }
  }
  let rev: number
  try {
    rev = await ревизия(свойId)
  } catch (e) {
    return { ok: false, вид: 'refused', почему: e instanceof Error ? e.message : String(e) }
  }
  const о = await вызов('/api/cards-write/stage-set', {
    method: 'POST',
    body: JSON.stringify({
      entity: 'deal', id: свойId, rev, stage_id: кодСтадии(bitrixStageId),
      ...(values ? { values } : {}),
    }),
  })
  if (о.status === 200) {
    return {
      ok: true,
      rev: Number(о.тело.rev) || rev + 1,
      стадия: (о.тело.stage ?? undefined) as (ИтогСтадии & { ok: true })['стадия'],
    }
  }
  if (о.status === 409) {
    const кто = String(о.тело.who ?? '').trim()
    return {
      ok: false, вид: 'conflict',
      // who бывает null (проба): тогда — слова ядра, они уже написаны для человека.
      почему: кто ? `успел(а) ${кто} — обновите карточку и повторите` : почемуИзТела(о),
    }
  }
  const поля = (о.тело.fields && typeof о.тело.fields === 'object'
    ? о.тело.fields : undefined) as Record<string, string> | undefined
  if (о.status === 400 && о.тело.error === 'refused' && поля && Object.keys(поля).length) {
    return { ok: false, вид: 'required', почему: почемуИзТела(о), поля }
  }
  return { ok: false, вид: 'refused', почему: почемуИзТела(о) }
}

/** Экранные коды Bitrix → коды полей своей схемы (cards_spec, карточка «сделка»).
 * ASSIGNED_BY_ID добавлен 01.08.2026 (вопрос владельца «как менять ответственного»):
 * пишется СВОЯ колонка deal.assigned_id (номер сотрудника СВОЕЙ схемы, не Bitrix),
 * зеркальное значение Bitrix остаётся исторической справкой рядом. */
const СВОИ_КОДЫ: Record<string, string> = {
  TITLE: 'title', OPPORTUNITY: 'amount', ASSIGNED_BY_ID: 'assigned_id',
}

/**
 * Правка системных полей сделки (TITLE, OPPORTUNITY) — в СВОЮ схему.
 *
 * POST /api/cards-write/update {entity: 'сделка', id: <свой>, rev, values}
 *   → 200 {id, rev, state, changed, row}; отказы — как у stage-set (409/400 refused).
 * Прежний write-through /api/deal/update (запись в Bitrix) отсюда убран: запись
 * из карточки идёт только в свою схему. STAGE_ID этим путём не ходит — у него
 * своя ручка со своим событием ленты (сменитьСтадию).
 * Ошибка бросается словами — её показывает строка поля.
 */
export async function сохранитьПоля(
  свойId: СвойId, fields: Record<string, unknown>,
): Promise<void> {
  if (!свойId) throw new Error(`${НЕТ_СВОЕГО_ID} — правку сохранить некуда.`)
  const values: Record<string, unknown> = {}
  for (const [код, значение] of Object.entries(fields)) {
    const свой = СВОИ_КОДЫ[код]
    if (!свой) {
      throw new Error(`поле ${код} из карточки в свою схему не пишется — правьте его там, `
        + 'где оно живёт')
    }
    // assigned_id — связь на сотрудника: колонка числовая, а FieldRow отдаёт строку.
    values[свой] = свой === 'assigned_id' ? Number(значение) : значение
  }
  const rev = await ревизия(свойId)
  const о = await вызов('/api/cards-write/update', {
    method: 'POST',
    body: JSON.stringify({ entity: СУЩНОСТЬ_КАРТОЧКИ.deal, id: свойId, rev, values }),
  })
  if (о.status === 200) return
  if (о.status === 409) {
    const кто = String(о.тело.who ?? '').trim()
    throw new Error(кто ? `успел(а) ${кто} — обновите карточку и повторите` : почемуИзТела(о))
  }
  throw new Error(почемуИзТела(о))
}

// ─────────────────────────── наши поля deal_ext ───────────────────────────

/**
 * Правка НАШЕГО поля (deal_ext) — этих полей нет в Bitrix, синк их не трогает.
 * Ручка прежняя и настоящая: POST /api/deal/ext {id: <bitrix>, patch} — deal_ext
 * ключуется по bitrix_id, а не по своему id (см. crm_own_api: донасыщение _EXT).
 */
export const сохранитьНашеПоле = async (
  bitrixId: string, code: string, value: unknown,
): Promise<void> => {
  await setDealExt(bitrixId, { [code]: value })
}

// ─────────────────────── сотрудники своей схемы (выбор ответственного) ───────────────────────

/** Один пункт выбора сотрудника: id — СВОЕЙ схемы (employee.id), имя — для списка. */
export interface ВыборСотрудника {
  id: string
  name: string
  /** Номер Bitrix того же человека — чтобы найти текущего по зеркальному значению. */
  bitrixId: string
}

/**
 * Активные сотрудники своей схемы для выпадашки «Ответственный».
 *
 * GET /api/own/refs?src=own → users: [{id: <bitrix>, name, last_name, active,
 * _OWN: {id: <свой>, is_system}}]. Берём только активных, не-системных и имеющих
 * след в своей схеме (_OWN.id — без него assigned_id писать некуда).
 */
export async function сотрудникиДляВыбора(): Promise<ВыборСотрудника[]> {
  const о = await вызов('/api/own/refs?src=own')
  if (о.status !== 200) throw new Error(почемуИзТела(о))
  const users = (о.тело as { users?: Array<Record<string, unknown>> }).users ?? []
  const итог: ВыборСотрудника[] = []
  for (const u of users) {
    const own = u._OWN as { id?: number; is_system?: boolean } | undefined
    if (!u.active || !own?.id || own.is_system) continue
    const имя = [u.last_name, u.name].filter(Boolean).join(' ').trim()
    итог.push({ id: String(own.id), name: имя || `сотрудник ${own.id}`,
                bitrixId: String(u.id ?? '') })
  }
  итог.sort((а, б) => а.name.localeCompare(б.name, 'ru'))
  return итог
}
